import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import workOrdersApi from "../../api/workOrdersApi";
import { useToastStack, ToastStack } from "../../../../shared/components/ActionToast";

// WO-CLOSING-PHOTOS-0414: extensions/MIMEs allowed by the Maintenance upload
// endpoint (CustomerWorkOrdersController.AllowedContentTypes). Listed here so
// the OS file picker filters by default — user can still toggle "All files".
const ACCEPTED_FILE_TYPES =
  "image/jpeg,image/png,image/gif,image/webp," +
  "application/pdf," +
  "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "text/plain,text/csv,text/markdown,text/rtf,application/rtf," +
  ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.md,.log,.rtf";

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: number;
  contentType: string;
}

interface CompletionDialogProps {
  open: boolean;
  workOrderId: string;
  workOrderNumber: string;
  hasMachine: boolean;
  hasLinkedSRs: boolean;
  machineName?: string;
  /**
   * WO-WORKER-EDIT-NOTES: "edit" mode re-purposes this dialog to update
   * CompletionNotes + Cost on a WO already in PendingReview(9). Defaults to
   * "complete" (submit-for-review) — all existing callers are unaffected.
   */
  mode?: "complete" | "edit";
  initialNotes?: string;
  initialCost?: number | null;
  onConfirm: (completionNotes: string, cost: number | undefined, createSR: boolean) => void;
  onCancel: () => void;
}

/**
 * Modal dialog for workers completing a WO (submitting for review) OR editing
 * closing details on a WO already in PendingReview.
 *
 * Modes:
 *   - "complete" (default): original behaviour — submits for review, may create an SR.
 *   - "edit": pre-fills notes + cost, hides the "Create SR" checkbox, swaps the
 *     primary button label. Caller routes onConfirm to the edit thunk.
 */
const CompletionDialog = ({
  open,
  workOrderId,
  workOrderNumber,
  hasMachine,
  hasLinkedSRs,
  machineName,
  mode = "complete",
  initialNotes,
  initialCost,
  onConfirm,
  onCancel,
}: CompletionDialogProps) => {
  const isEditMode = mode === "edit";
  const { translate } = useTranslation("operations");
  const [completionNotes, setCompletionNotes] = useState("");
  const [cost, setCost] = useState("");
  const [createSR, setCreateSR] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast, dismissToast, toasts } = useToastStack();

  // Reset on open — in edit mode, pre-fill from existing WO values.
  useEffect(() => {
    if (open) {
      setCompletionNotes(isEditMode ? (initialNotes ?? "") : "");
      setCost(
        isEditMode && initialCost != null && initialCost !== undefined
          ? String(initialCost)
          : ""
      );
      setCreateSR(false);
      setUploadedFiles([]);
      setUploading(false);
      setDragOver(false);
    }
  }, [open, isEditMode, initialNotes, initialCost]);

  // ─── File upload handler ──────────────────────────────────
  const handleFiles = useCallback(async (files: FileList | File[]) => {
    if (!workOrderId || files.length === 0) return;
    setUploading(true);
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      try {
        // WO-CLOSING-PHOTOS-0414: tag uploads from CompletionDialog as
        // ClosingRemarks so the WO detail page can render them in the green
        // ClosingReportCard carousel instead of the general attachments list.
        const result = await workOrdersApi.uploadFile(workOrderId, file, "ClosingRemarks");
        setUploadedFiles(prev => [...prev, {
          id: result.id || result.fileId || crypto.randomUUID(),
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type,
        }]);
      } catch (err: any) {
        // WO-CLOSING-PHOTOS-0414: surface the failure so users see WHY a file
        // was rejected (file type, size limit, magic-byte mismatch, etc.) —
        // previously the catch swallowed errors and the upload looked silent.
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.title;
        const message = serverMessage
          ? `${file.name}: ${serverMessage}`
          : translate("panel.uploadFailed", { fileName: file.name });
        addToast("error", message);
      }
    }
    setUploading(false);
  }, [workOrderId, addToast, translate]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  // Inject keyframes once
  useEffect(() => {
    const id = "completion-dialog-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes completionBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes completionSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!open) return null;

  const handleConfirm = () => {
    onConfirm(
      completionNotes,
      cost ? parseFloat(cost) : undefined,
      createSR
    );
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onCancel}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.25)",
          animation: "completionBackdropIn 0.2s ease",
        }}
      />

      {/* Dialog card */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "560px",
          mx: "16px",
          backgroundColor: "#FFFFFF",
          borderRadius: "20px",
          overflow: "hidden",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
          animation: "completionSlideUp 0.3s ease",
        }}
      >
        {/* Terracotta accent bar */}
        <Box
          sx={{
            height: "4px",
            background: "linear-gradient(90deg, #D4572B 0%, #B8451F 100%)",
          }}
        />

        {/* Content */}
        <Box sx={{ padding: "28px 28px 24px" }}>
          {/* Header row: title + close button */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <Box>
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#0C1117",
                  mb: "4px",
                }}
              >
                {isEditMode
                  ? translate("board.editClosingDetailsTitle")
                  : translate("myTasks.completionForm")}
              </Box>
              <Box
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#3B82F6",
                }}
              >
                {workOrderNumber}
              </Box>
            </Box>
            <Box
              onClick={onCancel}
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "1px solid #E8E5DF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
                flexShrink: 0,
                mt: "2px",
                "&:hover": {
                  borderColor: "#D1CCC4",
                  backgroundColor: "rgba(0,0,0,0.02)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "16px", color: "#8C9099" }} />
            </Box>
          </Box>

          {/* Closing Notes — required */}
          <Box sx={{ mt: "24px", mb: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
              }}
            >
              {translate("fields.completionNotes")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: "#D4572B",
              }}
            >
              {translate("fields.required")}
            </Box>
          </Box>
          <textarea
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            placeholder={translate("panel.completionNotesPlaceholder")}
            rows={4}
            style={{
              width: "100%",
              boxSizing: "border-box",
              fontFamily: "'Sora', sans-serif",
              fontSize: "14px",
              lineHeight: "1.6",
              color: "#0C1117",
              backgroundColor: "#F7F5F0",
              border: "1.5px solid transparent",
              borderRadius: "12px",
              padding: "14px 16px",
              outline: "none",
              resize: "vertical",
              minHeight: "100px",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#D4572B";
              e.target.style.boxShadow = "0 0 0 3px rgba(212,87,43,0.08)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "transparent";
              e.target.style.boxShadow = "none";
            }}
          />
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: "#8C9099",
              mt: "6px",
            }}
          >
            {translate("panel.completionNotesHint")}
          </Box>

          {/* Attachments — optional */}
          <Box sx={{ mt: "20px", mb: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
              }}
            >
              {translate("createPanel.attachments")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: "#8C9099",
              }}
            >
              {translate("fields.optional")}
            </Box>
          </Box>
          {/* Drop zone */}
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              backgroundColor: dragOver ? "rgba(212,87,43,0.04)" : "#F7F5F0",
              border: dragOver ? "1.5px dashed #D4572B" : "1.5px dashed #D1CCC4",
              borderRadius: "12px",
              padding: "24px 16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: "#D4572B",
                backgroundColor: "rgba(212,87,43,0.02)",
              },
            }}
          >
            {/* Plus icon */}
            <Box
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 8h10" stroke="#8C9099" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#4A4F57",
              }}
            >
              {translate("panel.dragFilesHere")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#8C9099",
              }}
            >
              {translate("panel.fileFormats")}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.length) {
                  handleFiles(e.target.files);
                  e.target.value = "";
                }
              }}
            />
          </Box>
          {/* Uploaded file list */}
          {(uploadedFiles.length > 0 || uploading) && (
            <Box sx={{ mt: "8px", display: "flex", flexDirection: "column", gap: "6px" }}>
              {uploadedFiles.map((f) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "#F7F5F0",
                    borderRadius: "8px",
                    padding: "8px 12px",
                  }}
                >
                  {/* File icon */}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                    <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z" stroke="#8C9099" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 1v4h4" stroke="#8C9099" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: "#4A4F57",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {f.fileName}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "11px",
                      color: "#8C9099",
                      flexShrink: 0,
                    }}
                  >
                    {f.fileSize < 1024 * 1024
                      ? `${(f.fileSize / 1024).toFixed(0)} KB`
                      : `${(f.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                  </Box>
                </Box>
              ))}
              {uploading && (
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#8C9099",
                    padding: "4px 12px",
                  }}
                >
                  {translate("panel.uploading")}
                </Box>
              )}
            </Box>
          )}

          {/* Cost — optional */}
          <Box sx={{ mt: "20px", mb: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
              }}
            >
              {translate("fields.cost")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                fontWeight: 500,
                color: "#8C9099",
              }}
            >
              {translate("fields.optional")}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#F7F5F0",
              borderRadius: "12px",
              border: "1.5px solid transparent",
              overflow: "hidden",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              "&:focus-within": {
                borderColor: "#D4572B",
                boxShadow: "0 0 0 3px rgba(212,87,43,0.08)",
              },
            }}
          >
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "13px",
                fontWeight: 600,
                color: "#8C9099",
                padding: "12px 14px",
                borderRight: "1px solid #E8E5DF",
                flexShrink: 0,
                userSelect: "none",
              }}
            >
              EUR
            </Box>
            <input
              type="number"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              placeholder="0.00"
              style={{
                flex: 1,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "14px",
                color: "#0C1117",
                backgroundColor: "transparent",
                border: "none",
                padding: "12px 14px",
                outline: "none",
                minWidth: 0,
              }}
            />
          </Box>

          {/* Create Service Report checkbox — only when machine is linked,
              no existing linked SRs, AND this is the initial completion flow.
              SR creation is a one-time event at submission; can't be retroactively
              triggered during an edit. */}
          {!isEditMode && hasMachine && !hasLinkedSRs && (
            <Box
              onClick={() => setCreateSR(!createSR)}
              sx={{
                mt: "20px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                backgroundColor: createSR ? "rgba(212,87,43,0.04)" : "#F7F5F0",
                borderRadius: "12px",
                padding: "14px 16px",
                cursor: "pointer",
                border: createSR ? "1.5px solid rgba(212,87,43,0.2)" : "1.5px solid transparent",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: createSR ? "rgba(212,87,43,0.06)" : "#F0EDE7",
                },
              }}
            >
              {/* Custom checkbox */}
              <Box
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "6px",
                  border: createSR ? "none" : "1.5px solid #D1CCC4",
                  backgroundColor: createSR ? "#D4572B" : "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.15s ease",
                }}
              >
                {createSR && (
                  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5L4.5 8.5L11 1.5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </Box>
              <Box>
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#0C1117",
                  }}
                >
                  {translate("actions.createServiceReport")}
                </Box>
                {machineName && (
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: "#8C9099",
                      mt: "2px",
                    }}
                  >
                    {translate("panel.generateSrFor")} {machineName}
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Action bar */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "16px", mt: "24px" }}>
            <Box
              onClick={onCancel}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: "pointer",
                padding: "8px 16px",
                borderRadius: "8px",
                transition: "color 0.15s ease",
                "&:hover": { color: "#4A4F57" },
              }}
            >
              {translate("actions.cancel")}
            </Box>
            <Box
              component="button"
              onClick={handleConfirm}
              disabled={!completionNotes.trim()}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#FFFFFF",
                backgroundColor: "#D4572B",
                border: "none",
                borderRadius: "24px",
                padding: "10px 28px",
                cursor: completionNotes.trim() ? "pointer" : "default",
                outline: "none",
                opacity: completionNotes.trim() ? 1 : 0.5,
                transition: "all 0.2s ease",
                "&:hover": completionNotes.trim()
                  ? {
                      backgroundColor: "#B8451F",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 16px rgba(212,87,43,0.3)",
                    }
                  : {},
              }}
            >
              {isEditMode
                ? translate("board.updateDetails")
                : translate("board.submitForReview")}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* WO-CLOSING-PHOTOS-0414: surface upload failures (file type,
          size, etc.) so they don't fail silently. */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </Box>
  );
};

export default CompletionDialog;
